import React from "react";
import {
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/clerk-react";
import { fmtDate, humanize, initialsOf } from "../lib/format";
import { getOrgLeaderboard } from "../lib/api";
import { isAdminRole } from "../lib/roles";
import type { AuthHeaders } from "../lib/auth";
import type { Org, OrgLeaderboard as OrgLeaderboardData } from "../lib/types";
import { TOKENS_SAVED_TIP } from "../lib/copy";
import {
  Card,
  EmptyState,
  Grid,
  Page,
  Pill,
  SectionHead,
  Skeleton,
  StatTile,
  Table,
  Tooltip,
} from "../design";

type Identity = { name: string; email: string | null };

type Row = {
  rank: number;
  clerkUserId: string | null;
  tokensSaved: number;
  sharePct: number;
  name: string;
  email: string | null;
  isSelf: boolean;
};

export function OrgLeaderboard({
  org,
  authHeaders,
}: {
  org: Org;
  authHeaders: AuthHeaders;
}) {
  const { user } = useUser();
  const myId = user?.id;
  const admin = isAdminRole(org.role);

  // Activate our org so Clerk's hooks resolve its memberships, matching
  // the admin panel — activate once instead of touching ClerkProvider.
  const { isLoaded: listLoaded, setActive } = useOrganizationList();
  const { organization } = useOrganization();
  const activated = organization?.id === org.clerk_org_id;
  React.useEffect(() => {
    if (!listLoaded || activated || !setActive) return;
    void setActive({ organization: org.clerk_org_id });
  }, [listLoaded, activated, setActive, org.clerk_org_id]);

  const [identities, setIdentities] = React.useState<Map<string, Identity>>(
    () => new Map(),
  );
  React.useEffect(() => {
    if (!activated || !organization) return;
    let cancelled = false;
    (async () => {
      const out = new Map<string, Identity>();
      const pageSize = 100;
      for (let page = 1; ; page += 1) {
        const res = await organization.getMemberships({
          initialPage: page,
          pageSize,
        });
        for (const m of res.data) {
          const pud = m.publicUserData;
          if (!pud?.userId) continue;
          const name = [pud.firstName, pud.lastName].filter(Boolean).join(" ");
          // Members see teammates by name only; emails stay with admins.
          out.set(pud.userId, {
            name: name || (admin ? pud.identifier : "A teammate"),
            email: admin ? pud.identifier || null : null,
          });
        }
        if (res.data.length < pageSize || page * pageSize >= res.total_count)
          break;
      }
      if (!cancelled) setIdentities(out);
    })().catch(() => {
      // Best-effort; rows fall back to a truncated clerk id.
    });
    return () => {
      cancelled = true;
    };
  }, [activated, organization, admin]);

  const [data, setData] = React.useState<OrgLeaderboardData | null>(null);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getOrgLeaderboard(authHeaders, org.org_id)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authHeaders, org.org_id]);

  if (loading || err)
    return (
      <Page title="Leaderboard">
        {err ? (
          <EmptyState icon="alert" title="Couldn't load the leaderboard">
            {err}
          </EmptyState>
        ) : (
          <Skeleton lines={6} />
        )}
      </Page>
    );

  const rows: Row[] = (data?.entries ?? []).map((e) => {
    const id = e.clerk_user_id;
    const ident = id ? identities.get(id) : undefined;
    const isSelf = !!myId && id === myId;
    // Members see only their own name; colleagues stay anonymous to them.
    const named = admin || isSelf;
    return {
      rank: e.rank,
      clerkUserId: id,
      tokensSaved: e.tokens_saved,
      sharePct: e.share_pct,
      name: named ? ident?.name || "Member" : `Teammate ${e.rank}`,
      email: named ? (ident?.email ?? null) : null,
      isSelf,
    };
  });

  const members = data?.member_count ?? rows.length;
  const totalSaved = data?.tokens_saved ?? 0;
  const leader = rows[0];

  return (
    <Page title="Leaderboard">
      <SectionHead
        title="Tokens saved this quarter"
        info={`${TOKENS_SAVED_TIP} Members see their own name and rank; colleagues appear as teammates. Admins see names. It is not a performance measure.`}
        sub={`${members} ${members === 1 ? "member" : "members"} ranked · since ${fmtDate(data?.window_start)}`}
      />
      {rows.length === 0 ? (
        <EmptyState icon="leaderboard" title="No savings yet this quarter">
          Run a session through condense and you'll show up here.
        </EmptyState>
      ) : (
        <>
          <Grid cols={3}>
            <StatTile
              label="Saved this quarter"
              value={
                <span title={`${totalSaved.toLocaleString()} tokens`}>
                  {humanize(totalSaved)}
                </span>
              }
              suffix="tokens"
            />
            <StatTile label="Members contributing" value={members} />
            <StatTile
              label="Most saved"
              value={
                <span className="ast-leader">
                  <span className="ast-avatar" aria-hidden>
                    {initialsOf(leader.name, leader.email ?? "")}
                  </span>
                  <span className="ast-leader-name">{leader.name}</span>
                </span>
              }
              note={`${humanize(leader.tokensSaved)} tokens saved`}
            />
          </Grid>
          <Card flush title="Ranking">
            <Table label="Leaderboard">
              <thead>
                <tr>
                  <th className="ast-rank">Rank</th>
                  <th>Member</th>
                  <th className="ast-share-cell">Share</th>
                  <th className="num">Tokens saved</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.clerkUserId ?? r.rank}
                    className={r.isSelf ? "is-selected" : undefined}
                  >
                    <td className={`ast-rank${r.rank <= 3 ? " top" : ""}`}>
                      {r.rank}
                    </td>
                    <td>
                      <span className="ast-member">
                        <span className="ast-avatar" aria-hidden>
                          {initialsOf(r.name, r.email ?? "")}
                        </span>
                        <span className="ast-member-text">
                          <span className="strong">
                            {r.name}
                            {r.isSelf && <Pill tone="info">You</Pill>}
                          </span>
                          {r.email && (
                            <span className="ds-cell-sub" title={r.email}>
                              {r.email}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="ast-share-cell">
                      <Tooltip
                        text={`${r.sharePct.toFixed(1)}% of ranked savings`}
                      >
                        <span
                          className={`ast-share${r.rank <= 3 ? " top" : ""}`}
                          aria-label={`${r.sharePct.toFixed(1)}% of ranked savings`}
                        >
                          <span
                            style={{ width: `${Math.max(r.sharePct, 2)}%` }}
                          />
                        </span>
                      </Tooltip>
                    </td>
                    <td
                      className="num strong"
                      title={`${r.tokensSaved.toLocaleString()} tokens`}
                    >
                      {humanize(r.tokensSaved)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </Page>
  );
}
